/**
 * Shared colour-name → hex map used by:
 *   - the seller/admin product wizard, to seed a swatch when the
 *     admin types a common colour name.
 *   - the storefront `ColorSwatchPicker` as a graceful fallback when
 *     the variant has no explicit `color_hex`.
 *
 * Keep this list small and canonical — brand-specific shade names
 * ("Warm Honey Beige") should be pinned via the admin's colour
 * picker instead of guessed here.
 */
export const NAME_TO_HEX = {
  // Base palette
  red: "#DC2626", blue: "#2563EB", green: "#16A34A", yellow: "#EAB308",
  orange: "#F97316", purple: "#9333EA", pink: "#DB2777", black: "#111111",
  white: "#F8FAFC", grey: "#9CA3AF", gray: "#9CA3AF", brown: "#78350F",
  gold: "#D4AF37", silver: "#C0C0C0", beige: "#EDD9B7", ivory: "#FFF6E5",
  navy: "#1E3A8A", teal: "#0D9488", cyan: "#06B6D4", maroon: "#7F1D1D",
  olive: "#4D7C0F", lime: "#84CC16",
  // Shade / foundation naming
  fair: "#F2DFC7", light: "#EAD1B0", medium: "#D9AE85", tan: "#C69065",
  deep: "#8A5A3B", ebony: "#3D251A",
  warm: "#D9A87A", cool: "#DDB79A", neutral: "#DFB79C",
  sand: "#E2C8A3", honey: "#D9A56B", caramel: "#B87345", chestnut: "#7A4A2E",
  porcelain: "#F5E0C3", nude: "#E6C6A0", almond: "#C3946A",
  bisque: "#EFD3B5", buff: "#DEC49A", espresso: "#4A2C1A",
};

/**
 * Return the canonical hex for a colour name (case- and space-tolerant).
 * Tries the full string first, then the last word so multi-word shade
 * names ("Warm Sand") still map to a sensible swatch.
 * Returns `null` when no match — callers can either render a text
 * chip or ask the admin to pin a hex.
 */
export function hexForColorName(name) {
  const k = (name || "").toLowerCase().trim();
  if (!k) return null;
  if (NAME_TO_HEX[k]) return NAME_TO_HEX[k];
  const last = k.split(/\s+/).pop();
  return NAME_TO_HEX[last] || null;
}

/** True when hex is `#rrggbb`. */
export function isValidHex(hex) {
  return /^#[0-9A-Fa-f]{6}$/.test(hex || "");
}

/**
 * Perceptual "lightness" gate — text/border contrast helper.
 * Returns true when the swatch is bright enough that a dark border
 * is required (e.g. white/ivory chips otherwise vanish into a light
 * background).
 */
export function isLightHex(hex) {
  if (!isValidHex(hex)) return false;
  const n = hex.replace("#", "");
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 186;
}

// Names a bare hex may be called by, each with a few anchor shades
// (web-standard + the palette above) so both "#ffa500" and "#F97316"
// land on Orange. Shade words ("fair", "warm") are deliberately absent
// — they read oddly as a standalone colour.
const LABEL_ANCHORS = [
  ["Red", "#FF0000", "#DC2626", "#B22222"],
  ["Maroon", "#800000", "#7F1D1D"],
  ["Pink", "#FFC0CB", "#FF69B4", "#FF1493", "#DB2777"],
  ["Orange", "#FFA500", "#FF8C00", "#F97316"],
  ["Yellow", "#FFFF00", "#FFD700", "#EAB308"],
  ["Gold", "#D4AF37"],
  ["Brown", "#8B4513", "#A0522D", "#78350F"],
  ["Beige", "#F5F5DC", "#F5DEB3", "#EDD9B7"],
  ["Green", "#008000", "#00FF00", "#228B22", "#16A34A"],
  ["Olive", "#808000", "#4D7C0F"],
  ["Teal", "#008080", "#0D9488"],
  ["Cyan", "#00FFFF", "#06B6D4"],
  ["Blue", "#0000FF", "#1E90FF", "#87CEEB", "#2563EB"],
  ["Navy", "#000080", "#1E3A8A"],
  ["Purple", "#800080", "#8A2BE2", "#EE82EE", "#9333EA"],
  ["Lavender", "#E6E6FA", "#C4B5FD"],
  ["Black", "#000000", "#111111"],
  ["White", "#FFFFFF", "#F8FAFC"],
  ["Grey", "#808080", "#404040", "#D3D3D3", "#9CA3AF"],
];

function _rgb(hex) {
  const n = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16));
}

/**
 * Shopper-facing text for a variant colour. Swatch-only variants use
 * the hex itself as their colour key (e.g. "#ff0000"), which reads
 * as noise in the cart — map those to the nearest base colour name
 * ("Red"). Named colours pass through untouched.
 */
export function colorLabel(value) {
  const v = (value || "").trim();
  if (!isValidHex(v)) return v;
  const [r, g, b] = _rgb(v);
  let best = v;
  let bestDist = Infinity;
  for (const [name, ...anchors] of LABEL_ANCHORS) {
    for (const anchor of anchors) {
      const [r2, g2, b2] = _rgb(anchor);
      // "Redmean" weighted distance — cheap and closer to perception
      // than plain RGB Euclidean.
      const rm = (r + r2) / 2;
      const d = (2 + rm / 256) * (r - r2) ** 2 + 4 * (g - g2) ** 2
        + (2 + (255 - rm) / 256) * (b - b2) ** 2;
      if (d < bestDist) { bestDist = d; best = name; }
    }
  }
  return best;
}
